import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ForcefieldLogo } from "@/components/ForcefieldLogo";

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "signup");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/analyze");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const mode = searchParams.get("mode");
    setIsLogin(mode !== "signup");
  }, [searchParams]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Authentication failed");
      }

      login(data.access_token, data.user);
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/analyze");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [isLogin, formData, login, navigate]);

  const toggleAuthMode = useCallback(() => {
    setIsLogin(prev => !prev);
  }, []);

  const getButtonText = () => {
    if (loading) {
      return <Loader2 className="w-5 h-5 animate-spin" />;
    }
    return isLogin ? "Sign In" : "Activate Forcefield";
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Left side - Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-violet-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-fuchsia-600/20 rounded-full blur-[100px]" />
        
        {/* Decorative elements - forcefield rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className="w-72 h-72 border border-violet-500/20 rounded-full animate-pulse" style={{ animationDuration: '3s' }} />
            <div className="w-56 h-56 border border-fuchsia-500/20 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{ animationDuration: '2.5s' }} />
            <div className="w-40 h-40 border border-violet-500/30 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{ animationDuration: '2s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <ForcefieldLogo className="w-24 h-24" />
            </div>
          </div>
        </div>

        {/* Floating text */}
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-sm text-violet-400 uppercase tracking-widest mb-4">/security activated/</p>
          <h2 className="font-outfit text-3xl font-bold text-white tracking-tight mb-4">
            Your Digital Forcefield Awaits
          </h2>
          <p className="text-gray-400">
            Analyze your social media presence for vulnerabilities and get actionable recommendations.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12" data-testid="auth-logo-link">
            <ForcefieldLogo className="w-10 h-10" />
            <span className="font-outfit font-bold text-xl text-white tracking-tight">Social <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Forcefield</span></span>
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-outfit text-3xl font-bold text-white tracking-tight mb-2">
              {isLogin ? "Welcome back" : "Create an account"}
            </h1>
            <p className="text-gray-400">
              {isLogin 
                ? "Enter your credentials to access your account" 
                : "Get started with your free security analysis"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 focus:border-violet-500/50 focus:ring-violet-500/20 rounded-xl"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 focus:border-violet-500/50 focus:ring-violet-500/20 rounded-xl"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 pr-12 focus:border-violet-500/50 focus:ring-violet-500/20 rounded-xl"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white h-12 text-base font-medium rounded-xl"
              data-testid="auth-submit-btn"
            >
              {getButtonText()}
            </Button>
          </form>

          {/* Switch mode */}
          <p className="mt-8 text-center text-gray-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              onClick={toggleAuthMode}
              className="text-violet-400 hover:text-violet-300 font-medium transition-colors"
              data-testid="switch-auth-mode-btn"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>

          {/* Powered by */}
          <p className="mt-12 text-center text-gray-600 text-sm">
            powered by <a href="https://agentseven.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">AgentSeven.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
}
